import { useCallback, useEffect, useState } from "react";
import { ElectronicDocumentDisablement } from "../../models/electronic-document-disablement";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import { getRoutePath } from "../../routes";
import DisablementUpdateModal from "./DisablementModal";
import DisablementDeleteModal from "./DisablementDeleteModal";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
} from "@coreui/react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import { DISABLEMENTS_CREATE } from "../../auth/permissions";
import { DOCUMENT_TYPE_CHOICES } from "../../models/documents";
import { useIsMobile } from "../../hooks/is-mobile";
import { ElectronicDocumentDisablementCard } from "./DisablementMobileCard";
import DisablementCrudStatus from "./DisablementCrudStatus";
import DisablementCrudActions from "./DisablementCrudActions";
import SystemNotificationBanners from "../notifications/SystemNotificationBanners";

const ITEMS_PER_PAGE = 10;

const DisablementCrud = () => {
  const fields = [
    { key: "stampIdentifier", _classes: ["text-center"], label: "Timbrado Nº" },
    {
      key: "establishmentCode",
      _classes: ["text-center"],
      label: "Establecimiento",
    },
    {
      key: "dispatchPointIdentifier",
      _classes: ["text-center"],
      label: "Punto de expedición",
    },
    {
      key: "documentType",
      _classes: ["text-center"],
      label: "Tipo de Documento",
    },
    {
      key: "from",
      _classes: ["text-center"],
      label: "Desde",
    },
    {
      key: "to",
      _classes: ["text-center"],
      label: "Hasta",
    },
    {
      key: "reason",
      _classes: ["text-center"],
      label: "Motivo",
    },
    {
      key: "status",
      _classes: ["text-center"],
      label: "Estado",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [item, setItem] = useState<ElectronicDocumentDisablement | undefined>(
    undefined,
  );

  const [items, setItems] = useState<ElectronicDocumentDisablement[]>([]);

  const [loading, setLoading] = useState(true);

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();
  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }

    return { page: page };
  };

  const fetchItems = useCallback(async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.page - 1);
    const additionalParams = new Map<string, string>();
    additionalParams.set("tax_payer_id", taxPayer.id!.toString());
    const itemsStatus = await getList<ElectronicDocumentDisablement>(
      "/disablements/",
      limit,
      offset,
      additionalParams,
    );

    if (itemsStatus.status === SUCCESS) {
      if (itemsStatus.data !== undefined) {
        const count = itemsStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setItems(itemsStatus.data.items);
        setCurrentPage(urlParams.page);
      }
    } else {
      const message = itemsStatus.detail
        ? itemsStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  /*const reset = () => {
    history.push(history.location.pathname);
  };*/

  const onAddClick = () => {
    setItem(undefined);
    setShowEditModal(true);
  };

  const onUpdateClick = (item: ElectronicDocumentDisablement) => {
    setItem(item);
    setShowEditModal(true);
  };

  const onDeleteClick = (establishment: ElectronicDocumentDisablement) => {
    setItem(establishment);
    setShowDeleteModal(true);
  };

  const onEditCancel = () => {
    setShowEditModal(false);
  };

  const onEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowEditModal(false);
    setItems([]);
    setLoading(true);
    //setTotalPages(1);
    //setCurrentPage(1);
    //reset();
    fetchItems();
  };

  const onDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const onDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowDeleteModal(false);
    setItems([]);
    setLoading(true);
    fetchItems();
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("disablements")) {
        return;
      }
      fetchItems();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  useEffect(() => {
    setLoading(true);
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  const isMobile = useIsMobile();

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Inutilizaciones</h3>
              </div>
              <div className="p-2 ">
                <PermissionRequiredComponent
                  permissionName={DISABLEMENTS_CREATE}
                >
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Nuevo
                  </CButton>
                </PermissionRequiredComponent>
              </div>
            </CCardHeader>
            <SystemNotificationBanners />
            <CCardBody>
              <div>
                {isMobile ? (
                  // Mobile view: collapsible cards
                  <>
                    {items.length === 0 ? (
                      <h2 className="text-center">
                        No se encontraron registros
                      </h2>
                    ) : (
                      items.map((entry) => (
                        <ElectronicDocumentDisablementCard
                          key={entry.id}
                          disablement={entry}
                          onUpdateClick={onUpdateClick}
                          onDeleteClick={onDeleteClick}
                        />
                      ))
                    )}
                  </>
                ) : (
                  <CDataTable
                    noItemsView={{
                      noItems: "No se encontraron registros",
                      noResults: "No se encontraron registros",
                    }}
                    addTableClasses={"table-fixed"}
                    fields={fields}
                    items={items}
                    striped
                    border
                    loading={loading}
                    responsive
                    scopedSlots={{
                      documentType: (item: ElectronicDocumentDisablement) => {
                        let label = DOCUMENT_TYPE_CHOICES.get(
                          item.documentType!,
                        );
                        if (label === null) {
                          label = "-";
                        }
                        return <td className="text-center">{label}</td>;
                      },
                      status: (item: ElectronicDocumentDisablement) => (
                        <DisablementCrudStatus disbablement={item} />
                      ),
                      actions: (item: ElectronicDocumentDisablement) => (
                        <DisablementCrudActions
                          disablement={item}
                          onUpdateClick={onUpdateClick}
                          onDeleteClick={onDeleteClick}
                        />
                      ),
                    }}
                  />
                )}
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <DisablementUpdateModal
        show={showEditModal}
        onCancel={onEditCancel}
        onSuccess={onEditSuccess}
        disablement={item}
      />
      <DisablementDeleteModal
        show={showDeleteModal}
        onCancel={onDeleteCancel}
        onSuccess={onDeleteSuccess}
        disablement={item}
      />
    </>
  );
};

export default DisablementCrud;
